'use client';

import React, {
  useRef,
  useEffect,
  createContext,
  useContext,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  size?: 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'base2';
  hideBackdrop?: boolean;
  clickOutside?: boolean;
  /** Called instead of onClose when close is triggered by clicking outside.
   *  The consumer can show a confirmation dialog and call onClose() manually if confirmed.
   *  When not provided, onClose is called directly (current behavior). */
  onBeforeClose?: () => void;
  showCloseButton?: boolean;
  className?: string;
  variant?: 'temporary' | 'persistent';
}

const DrawerContext = createContext<{ onClose: () => void } | undefined>(
  undefined
);

const DrawerCloseButton = ({ className = '' }: { className?: string }) => {
  const context = useContext(DrawerContext);
  if (!context) throw new Error('CloseButton must be used within a Drawer');

  return (
    <button
      onClick={context.onClose}
      className={`w-5 h-5 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors ${className}`}
      aria-label="Close drawer"
    >
      <Icon icon={faTimes} className="w-4 h-4" />
    </button>
  );
};

const Drawer = ({
  isOpen,
  onClose,
  children,
  anchor = 'right',
  size = 'base',
  hideBackdrop = false,
  showCloseButton = false,
  clickOutside = true,
  onBeforeClose,
  className = '',
  variant = 'temporary',
}: DrawerProps) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else {
      // For persistent drawers, unmount immediately since they don't need slide-out animations
      // For temporary drawers, wait 300ms to allow slide-out animation to complete
      const delay = variant === 'persistent' ? 0 : 300;
      const timer = setTimeout(() => {
        setMounted(false);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, variant]);

  useEffect(() => {
    if (!isOpen || !clickOutside || variant !== 'temporary') return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const portalTarget = (event.target as Element)?.closest(
        '[data-select-portal="true"], [data-dialog-portal="true"]'
      );

      if (
        drawerRef.current &&
        !drawerRef.current.contains(target) &&
        !portalTarget
      ) {
        (onBeforeClose ?? onClose)();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, clickOutside, variant, onClose, onBeforeClose]);

  // For persistent drawers, immediately return null when closed to avoid layout conflicts
  // For temporary drawers, use the mounted state to allow animations
  if (variant === 'persistent' && !isOpen) return null;
  if (!mounted && !isOpen) return null;

  const shouldShowBackdrop = variant === 'temporary' && !hideBackdrop;
  const shouldHandleClickOutside = variant === 'temporary' && clickOutside;

  const sizeClasses = {
    base: 'w-base', // 440px
    base2: 'w-base2', // 400px
    sm: 'w-80', // 320px
    md: 'w-96', // 384px (+64)
    lg: 'w-128', // 512px (+128)
    xl: 'w-160', // 640px (+128)
    '2xl': 'w-192', // 768px (+128)
    '3xl': 'w-224', // Use the new config value (896px)
  };

  const heightClasses = {
    sm: 'h-80', // 320px
    md: 'h-96', // 384px (+64)
    lg: 'h-128', // 512px (+128)
    xl: 'h-160', // 640px (+128)
    '2xl': 'h-192', // 768px (+128)
    '3xl': 'h-224', // Use the new config value (896px)
  };

  const isVertical = anchor === 'left' || anchor === 'right';

  const getAnimationClasses = () => {
    if (isOpen) {
      return {
        right: 'animate-slide-in-right',
        left: 'animate-slide-in-left',
        top: 'animate-slide-in-top',
        bottom: 'animate-slide-in-bottom',
      }[anchor];
    }
    return mounted
      ? {
          right: 'animate-slide-out-right',
          left: 'animate-slide-out-left',
          top: 'animate-slide-out-top',
          bottom: 'animate-slide-out-bottom',
        }[anchor]
      : '';
  };

  const baseStyle: React.CSSProperties = {
    '--drawer-edge': 'calc(100% + 10px)',
  } as any;

  const drawerStyle: React.CSSProperties =
    variant === 'temporary'
      ? {
          ...baseStyle,
          top: 0,
          bottom: 0,
        }
      : baseStyle;

  const drawerContent = (
    <div
      ref={drawerRef}
      data-testid="drawer-content"
      style={drawerStyle as any}
      className={twMerge(
        'flex flex-col bg-white shadow-xl z-10 overflow-hidden',
        isVertical ? sizeClasses[size] : heightClasses[size],
        variant === 'persistent' ? 'relative h-full max-h-full' : 'fixed',
        variant === 'temporary' &&
          {
            right: 'right-0',
            left: 'left-0',
            top: 'top-0',
            bottom: 'bottom-0',
          }[anchor],
        anchor === 'right' && 'rounded-tl-lg',
        anchor === 'left' && 'rounded-tr-lg',
        anchor === 'top' && 'rounded-b-lg',
        anchor === 'bottom' && 'rounded-t-lg',
        getAnimationClasses(),
        !isOpen && !mounted && 'pointer-events-none',
        className
      )}
    >
      {showCloseButton && (
        <Drawer.CloseButton className="absolute top-1 right-1 z-30" />
      )}
      {/* Add safe area padding for iOS devices with notches/Dynamic Island */}
      <div className="relative flex flex-col h-full min-h-0 overflow-hidden pb-[env(safe-area-inset-bottom,_0px)]">
        {children}
      </div>
    </div>
  );

  if (variant === 'temporary') {
    return createPortal(
      <DrawerContext.Provider value={{ onClose }}>
        <div
          className={`fixed inset-0 z-[9999] ${
            mounted && isOpen ? 'opacity-100' : 'opacity-0'
          } transition-opacity duration-300 ease-drawer ${
            !(mounted && isOpen) && 'pointer-events-none'
          }`}
        >
          <div
            className={`absolute inset-0 ${
              !shouldShowBackdrop
                ? 'bg-transparent'
                : 'bg-black/50 backdrop-blur-sm'
            } ${
              mounted &&
              isOpen &&
              shouldHandleClickOutside &&
              shouldShowBackdrop
                ? 'pointer-events-auto'
                : 'pointer-events-none'
            }`}
            onClick={shouldHandleClickOutside ? (onBeforeClose ?? onClose) : undefined}
          />
          <div className={`relative w-full h-full ${shouldShowBackdrop ? 'pointer-events-auto' : 'pointer-events-none'}`}>
            <div className="pointer-events-auto">
              {drawerContent}
            </div>
          </div>
        </div>
      </DrawerContext.Provider>,
      document.body
    );
  }

  return (
    <DrawerContext.Provider value={{ onClose }}>
      <div className="w-fit h-full">{drawerContent}</div>
    </DrawerContext.Provider>
  );
};
DrawerCloseButton.displayName = 'Drawer.CloseButton';

const DrawerHeader = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`
        p-4
        flex
        items-start
        justify-between
        shrink-0
        border-b
        border-neutral-200
        ${className}
      `}
  >
    {children}
  </div>
);
DrawerHeader.displayName = 'Drawer.Header';

const DrawerContent = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={twMerge(
      'flex-1 flex flex-col min-h-0 overflow-y-auto',
      className
    )}
  >
    {children}
  </div>
);
DrawerContent.displayName = 'Drawer.Content';

const DrawerFooter = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`
        border-t
        border-neutral-300
        flex
        items-center
        justify-end
        px-6
        py-6
        gap-2
        shrink-0
        sticky
        bottom-0
        z-10
        ${className}
      `}
  >
    {children}
  </div>
);
DrawerFooter.displayName = 'Drawer.Footer';

Drawer.CloseButton = DrawerCloseButton;
Drawer.Header = DrawerHeader;
Drawer.Content = DrawerContent;
Drawer.Footer = DrawerFooter;

export default Drawer;
